import { useEffect, useRef, RefObject, MutableRefObject } from "react";
import {
  ClientPosition,
  SmoothZoomHandler,
  ZoomPointerEvent,
} from "../types/zoom";

const DEFAULT_MINIMUM_PINCH_RATIO = 1.01;
const PINCH_ZOOM_IN_STEP = 1.05;
const PINCH_ZOOM_OUT_STEP = 0.95;
const NO_SCALE_CHANGE = 1;

interface PinchBaseline {
  distance: number;
  midpoint: ClientPosition;
}

interface UsePinchZoomParams {
  containerRef: RefObject<HTMLElement>;
  onPinch: SmoothZoomHandler;
  isPinchingRef: MutableRefObject<boolean>;
  minimumPinchRatio?: number;
}

interface UsePinchZoomReturn {
  onPointerDown: (event: ZoomPointerEvent) => boolean;
  onPointerMove: (event: ZoomPointerEvent) => boolean;
  onPointerUp: (event: ZoomPointerEvent) => void;
  onPointerCancel: (event: ZoomPointerEvent) => void;
}

function getPointerDistance(pointers: Map<number, ClientPosition>): number {
  const values = Array.from(pointers.values());
  const horizontalDistance = values[0].clientX - values[1].clientX;
  const verticalDistance = values[0].clientY - values[1].clientY;
  return Math.sqrt(
    horizontalDistance * horizontalDistance +
      verticalDistance * verticalDistance,
  );
}

function getPointerMidpoint(
  pointers: Map<number, ClientPosition>,
): ClientPosition {
  const values = Array.from(pointers.values());
  return {
    clientX: (values[0].clientX + values[1].clientX) / 2,
    clientY: (values[0].clientY + values[1].clientY) / 2,
  };
}

export default function usePinchZoom({
  containerRef,
  onPinch,
  isPinchingRef,
  minimumPinchRatio = DEFAULT_MINIMUM_PINCH_RATIO,
}: UsePinchZoomParams): UsePinchZoomReturn {
  const activePointersRef = useRef<Map<number, ClientPosition>>(new Map());
  const pinchBaselineRef = useRef<PinchBaseline | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleTouchMove = (e: TouchEvent) => {
      if (isPinchingRef.current) {
        e.preventDefault();
      }
    };

    container.addEventListener("touchmove", handleTouchMove, {
      passive: false,
    });

    return () => {
      container.removeEventListener("touchmove", handleTouchMove);
    };
  }, [containerRef, isPinchingRef]);

  const onPointerDown = (event: ZoomPointerEvent): boolean => {
    activePointersRef.current.set(event.pointerId, {
      clientX: event.clientX,
      clientY: event.clientY,
    });

    if (activePointersRef.current.size === 2) {
      isPinchingRef.current = true;

      if (containerRef.current) {
        containerRef.current.style.touchAction = "none";
      }

      Array.from(activePointersRef.current.keys()).forEach((id) => {
        try {
          containerRef.current.setPointerCapture(id);
        } catch {
          /* pointer may already be captured */
        }
      });
      pinchBaselineRef.current = {
        distance: getPointerDistance(activePointersRef.current),
        midpoint: getPointerMidpoint(activePointersRef.current),
      };
      return true;
    }

    return false;
  };

  const onPointerMove = (event: ZoomPointerEvent): boolean => {
    if (activePointersRef.current.has(event.pointerId)) {
      activePointersRef.current.set(event.pointerId, {
        clientX: event.clientX,
        clientY: event.clientY,
      });
    }

    if (activePointersRef.current.size !== 2 || !pinchBaselineRef.current) {
      return false;
    }

    const currentDistance = getPointerDistance(activePointersRef.current);
    const ratio = currentDistance / pinchBaselineRef.current.distance;
    const pinchExceedsMinimumRatio =
      Math.abs(ratio - NO_SCALE_CHANGE) > minimumPinchRatio - NO_SCALE_CHANGE;

    if (pinchExceedsMinimumRatio) {
      const { clientX, clientY } = pinchBaselineRef.current.midpoint;
      const zoomDirection =
        ratio > NO_SCALE_CHANGE ? PINCH_ZOOM_IN_STEP : PINCH_ZOOM_OUT_STEP;
      onPinch(zoomDirection, clientX, clientY);

      pinchBaselineRef.current = {
        distance: currentDistance,
        midpoint: getPointerMidpoint(activePointersRef.current),
      };
    }

    return true;
  };

  const onPointerUp = (event: ZoomPointerEvent): void => {
    activePointersRef.current.delete(event.pointerId);
    try {
      containerRef.current.releasePointerCapture(event.pointerId);
    } catch {
      /* pointer capture may already be released */
    }
    if (activePointersRef.current.size < 2) {
      pinchBaselineRef.current = null;
      if (activePointersRef.current.size === 0) {
        isPinchingRef.current = false;

        if (containerRef.current) {
          containerRef.current.style.touchAction = "";
        }
      }
    }
  };

  const onPointerCancel = (event: ZoomPointerEvent): void => {
    activePointersRef.current.delete(event.pointerId);
    pinchBaselineRef.current = null;
  };

  return { onPointerDown, onPointerMove, onPointerUp, onPointerCancel };
}
